import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { resetTour, TOUR_DEFINITIONS, isTourCompleted } from '../services/tourService';
import { 
  HelpCircle, 
  Play, 
  CheckCircle, 
  LayoutDashboard, 
  ShoppingCart, 
  Truck,
  Users, 
  Wallet,
  BarChart3,
  UserCheck,
  Eye,
  X
} from 'lucide-react';

// Icon mapping for tour types
const TOUR_ICONS = {
  dashboard: LayoutDashboard,
  viewEye: Eye,
  generateOrder: ShoppingCart,
  rawMaterialOrder: Truck,
  ledgerManagement: Wallet,
  reports: BarChart3,
  attendance: UserCheck,
  staffManagement: Users,
};

/**
 * StartTourDialog Component
 * 
 * Shows available tours with completion status
 * Allows user to restart any tour
 */
const StartTourDialog = ({ open, onOpenChange, onStartTour }) => {
  const navigate = useNavigate();
  const tours = Object.values(TOUR_DEFINITIONS);

  const handleStartTour = (tourId) => {
    resetTour(tourId);
    onOpenChange(false);
    if (onStartTour) {
      onStartTour(tourId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-amber-500" />
            Start a Guided Tour
          </DialogTitle>
          <DialogDescription>
            Learn how to use different features with our guided tours.
            You can restart a tour at any time.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-3 py-2">
            {tours.map((tour) => {
              const Icon = TOUR_ICONS[tour.id] || HelpCircle;
              const completed = isTourCompleted(tour.id);
              
              return (
                <div
                  key={tour.id}
                  className={`
                    p-4 rounded-lg border transition-all
                    ${completed 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-white border-gray-200 hover:border-amber-300 hover:shadow-sm'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`
                        p-2 rounded-lg 
                        ${completed ? 'bg-green-100' : 'bg-amber-50'}
                      `}>
                        <Icon className={`h-5 w-5 ${completed ? 'text-green-600' : 'text-amber-600'}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{tour.name}</h4>
                        <p className="text-xs text-gray-500">
                          {tour.steps.length} steps
                          {completed && (
                            <span className="inline-flex items-center ml-2 text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Completed
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <Button
                      size="sm"
                      variant={completed ? "outline" : "default"}
                      onClick={() => handleStartTour(tour.id)}
                      className={completed ? '' : 'bg-amber-500 hover:bg-amber-600'}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      {completed ? 'Restart' : 'Start'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex justify-end pt-2 border-t">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-1" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StartTourDialog;
